import type {
    PtV2Dataset,
    PtV2DatasetKey,
    PtV2ImportRecord,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";

/** Every dataset the page can use, in page order: what it is called, where it shows, and how it can be loaded. */
export const PT_V2_DATASET_USAGE: {
    dataset: PtV2DatasetKey;
    label: string;
    /** The tabs and sections that read it; they show no data without it. */
    usedIn: string[];
    /** Published to BigQuery by Hermes; the others come only with a zip upload. */
    inBigQuery: boolean;
}[] = [
    {dataset: 'leakage', label: 'Payment term leakage register', inBigQuery: true,
        usedIn: ['Overview KPIs', 'Payment term leakage', 'Action plan']},
    {dataset: 'harmonization', label: 'DPO harmonization targets', inBigQuery: true,
        usedIn: ['Overview KPIs', 'Term harmonization', 'Action plan']},
    {dataset: 'term_mix', label: 'Supplier term mix', inBigQuery: true,
        usedIn: ['Term harmonization (term mix per supplier)']},
    {dataset: 'discounts', label: 'Prompt discount capture register', inBigQuery: true,
        usedIn: ['Overview KPIs', 'Discount capture']},
    {dataset: 'distribution', label: 'ERP term distribution', inBigQuery: true,
        usedIn: ['Overview KPIs', 'Overview term distribution']},
    {dataset: 'contract_profile', label: 'Contract term profile', inBigQuery: true,
        usedIn: ['Overview', 'Data sources (data layers, PDF coverage)']},
    {dataset: 'catalog', label: 'SAP term catalog', inBigQuery: true,
        usedIn: ['Data sources (SAP payment term catalog)']},
    {dataset: 'playbook', label: 'Working capital playbook', inBigQuery: false,
        usedIn: ['Action plan (recommended actions)']},
    {dataset: 'taxonomy', label: 'Payment terms taxonomy', inBigQuery: false,
        usedIn: []},
];

export type PtV2WorkpaperState = 'updated' | 'unchanged' | 'loaded' | 'missing';

export type PtV2WorkpaperRow = (typeof PT_V2_DATASET_USAGE)[number] & {
    /** The loaded copy, if any. */
    loaded: PtV2Dataset | null;
    /** `updated`/`unchanged`: by the latest sync; `loaded`: loaded before (or from a zip); `missing`: not loaded. */
    state: PtV2WorkpaperState;
};

/** One row per dataset the page can use: whether it is loaded, what the latest sync did with it, where it shows. */
export function workpaperCoverage(datasets: PtV2Dataset[], lastSync: PtV2ImportRecord | null): PtV2WorkpaperRow[] {
    const synced = new Map((lastSync?.status === 'succeeded' ? lastSync.datasets : []).map(d => [d.dataset, d]));
    return PT_V2_DATASET_USAGE.map(usage => {
        const loaded = datasets.find(d => d.dataset === usage.dataset) ?? null;
        const run = synced.get(usage.dataset);
        let state: PtV2WorkpaperState = loaded ? 'loaded' : 'missing';
        if (loaded && run && run.slug === loaded.slug) state = run.changed === false ? 'unchanged' : 'updated';
        return {...usage, loaded, state};
    });
}

/** "3 updated, 4 unchanged. Not published in Hermes: Supplier term mix (Term harmonization …). Ignored: opp_x." */
export function syncSummary(run: PtV2ImportRecord): string {
    const found = run.datasets ?? [];
    const updated = found.filter(d => d.changed !== false).length;
    const unchanged = found.length - updated;
    const parts = [`${updated} workpaper${updated === 1 ? '' : 's'} updated, ${unchanged} unchanged.`];
    const missing = PT_V2_DATASET_USAGE.filter(u => u.inBigQuery && !found.some(d => d.dataset === u.dataset));
    if (missing.length) {
        parts.push(`Not published in Hermes: ${missing.map(u => `${u.label} (${u.usedIn.join(', ')})`).join('; ')}.`);
    }
    if (run.skipped?.length) parts.push(`Published but not used by this page: ${run.skipped.join(', ')}.`);
    return parts.join(' ');
}
